import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { sv } from "date-fns/locale";

import { SeminarService } from "../../../DataService/SeminarService";
import { DataService } from "../../../DataService/DataService";
import { useUser } from "../../../DataService/UserContext";
import { apiGet } from "../../../DataService/apiClient";
import EmailTemplateService, { EmailTemplateDto } from "../../../DataService/EmailTemplateService";
import { MemberBookingHistory, MemberHistoryResponse } from "../../../Types/MemberHistoryTypes";
import { SeminarDto } from "../../../Types/SeminarServiceTypes";
import { UserCourseListItem } from "../../../Types/UserCourseTypes";
import "../MemberDashboard.css";

export const DashboardOverview = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [heroImage] = useState(() => {
    const images = [
      "/images/wellness_hero_1.png",
      "/images/wellness_hero_2.png",
      "/images/wellness_hero_3.png",
      "/images/wellness_hero_4.png",
      "/images/wellness_hero_5.png",
      "/images/wellness_hero_6.png"
    ];

    return images[Math.floor(Math.random() * images.length)];
  });

  const [history, setHistory] = useState<MemberHistoryResponse | null>(null);
  const [bookings, setBookings] = useState<MemberBookingHistory[]>([]);
  const [seminars, setSeminars] = useState<SeminarDto[]>([]);
  const [courses, setCourses] = useState<UserCourseListItem[]>([]);
  const [messages, setMessages] = useState<EmailTemplateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [counterRequestId, setCounterRequestId] = useState<number | null>(null);
  const [counterDate, setCounterDate] = useState("");
  const [counterTime, setCounterTime] = useState("");
  const [counterSlots, setCounterSlots] = useState<Array<{ date: string; startTime: string; category: string }>>([]);

  useEffect(() => {
    void loadData();
  }, []);

  const loadData = async () => {
    try {
      const [memberHistory, myCourses, mySeminars, feelgood] = await Promise.all([
        apiGet<MemberHistoryResponse>("/Auth/me/history"),
        apiGet<UserCourseListItem[]>("/UserCourse/my-courses"),
        SeminarService.GetMySeminars(),
        EmailTemplateService.getVisible()
      ]);

      setHistory(memberHistory ?? null);
      setBookings(memberHistory?.bookings ?? []);
      setCourses(myCourses ?? []);
      setSeminars(mySeminars ?? []);
      setMessages((feelgood ?? [])
        .slice()
        .sort((a, b) => {
          const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bDate - aDate;
        }));
    } catch (err) {
      console.error("Dashboard load error", err);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="dashboard-section">
        <p>Laddar dashboard...</p>
      </div>
    );
  }

  const nextCourse = courses
    .slice()
    .sort(
      (a, b) =>
        b.completedChapters / Math.max(b.totalChapters, 1) -
        a.completedChapters / Math.max(a.totalChapters, 1)
    )[0];

  const nextCourseProgress = nextCourse
    ? Math.round((nextCourse.completedChapters / Math.max(nextCourse.totalChapters, 1)) * 100)
    : 0;

  const nextBooking = bookings
    .slice()
    .sort((a, b) => {
      const aDate = new Date(`${a.date}T${a.startTime}`).getTime();
      const bDate = new Date(`${b.date}T${b.startTime}`).getTime();
      return aDate - bDate;
    })[0];

  const nextSeminar = seminars
    .slice()
    .sort((a, b) => {
      const aDate = Date.parse(`${a.date ?? ""} ${a.time ?? ""}`) || 0;
      const bDate = Date.parse(`${b.date ?? ""} ${b.time ?? ""}`) || 0;
      return aDate - bDate;
    })[0];

  const activeRebookRequests = (history?.rebookRequests ?? [])
    .filter((request) => !request.isClosed && new Date(request.expiresAt).getTime() > Date.now())
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const expiredRebookRequests = (history?.rebookRequests ?? [])
    .filter((request) => !request.isClosed && new Date(request.expiresAt).getTime() <= Date.now())
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const getRequestStatusText = (status: string) => {
    switch (status) {
      case "Pending": return "Väntar på ditt svar";
      case "Countered": return "Ditt motförslag är skickat";
      case "Accepted": return "Accepterad";
      case "Declined": return "Nekad";
      default: return status;
    }
  };

  const getCategoryText = (category?: string) => {
    switch (category) {
      case "rot": return "ROT";
      case "grow": return "GROW";
      case "__system-rot": return "ROT";
      default: return "Publik";
    }
  };

  const buildRebookAcceptUrl = (requestId: number, slot: { id: number; date: string; startTime: string; endTime: string }, category: string) => {
    const params = new URLSearchParams({
      requestId: String(requestId),
      slotId: String(slot.id),
      date: slot.date,
      startTime: slot.startTime,
      endTime: slot.endTime,
      category
    });

    return `/rebook/accept?${params.toString()}`;
  };

  const addCounterSlot = () => {
    const request = activeRebookRequests.find((item) => item.id === counterRequestId);
    const category = request?.bookingCategory ?? "public";

    if (!counterDate || !counterTime) return;

    setCounterSlots((prev) => {
      const exists = prev.some((slot) => slot.date === counterDate && slot.startTime === counterTime);
      if (exists) return prev;
      return [...prev, { date: counterDate, startTime: counterTime, category }];
    });

    setCounterTime("");
  };

  const submitCounterOffer = async (requestId: number) => {
    const ok = await DataService.BookingService.CounterRebookProposal(requestId, counterSlots);
    if (ok) {
      setCounterSlots([]);
      setCounterRequestId(null);
      setCounterDate("");
      setCounterTime("");
      void loadData();
    }
  };

  const latestMessage = messages[0];

  const stats = [
    { label: "Kurser", value: String(courses.length) },
    { label: "Bokningar", value: String(bookings.length) },
    { label: "Seminarier", value: String(seminars.length) },
    { label: "Reflektioner", value: String(messages.length) }
  ];

  return (
    <div className="dashboard-section">
      <section className="dashboard-hero dashboard-card dashboard-hero-panel">
        <div>
          <p className="dashboard-callout-kicker">Välkommen tillbaka</p>
          <h1 className="dashboard-title">Välkommen tillbaka</h1>
          <p className="dashboard-subtitle">Här fortsätter du där du är.</p>
        </div>

        <div className="dashboard-hero-visual">
          <div className="dashboard-hero-visual-card" style={{ overflow: "hidden" }}>
            <img
              src={heroImage}
              alt="Wellnessbild i dashboarden"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </div>
        </div>
      </section>

      <section className="dashboard-stat-grid">
        {stats.map((stat) => (
          <article key={stat.label} className="dashboard-summary-card">
            <span>{stat.label}</span>
            <strong>{stat.value}</strong>
          </article>
        ))}
      </section>

      <section className="dashboard-overview-grid">
        <article className="dashboard-card dashboard-card--highlight">
          <p className="dashboard-callout-kicker">Fortsätt här</p>
          <h3>{nextCourse ? nextCourse.title : "Inget kurssteg ännu"}</h3>
          <p>
            {nextCourse
              ? `${nextCourse.completedChapters} av ${nextCourse.totalChapters} kapitel klara`
              : "När en kurs öppnas visas den här."}
          </p>

          {nextCourse && (
            <>
              <div className="dashboard-progress dashboard-progress--large">
                <div className="dashboard-progress-bar" style={{ width: `${nextCourseProgress}%` }} />
              </div>
              <button type="button" className="dashboard-course-cta" onClick={() => navigate(`/member/courses/${nextCourse.courseId}`)}>
                Fortsätt kurs
              </button>
            </>
          )}
        </article>

        <article className="dashboard-card">
          <p className="dashboard-callout-kicker">Dagens reflektion</p>
          <h3>{latestMessage?.name ?? "Ett litet stöd"}</h3>
          <p className="dashboard-feelgood-subject">{latestMessage?.subject ?? "Vi fyller på med nytt stöd när det publiceras."}</p>
          {latestMessage && (
            <div className="dashboard-feelgood-body" dangerouslySetInnerHTML={{ __html: latestMessage.htmlContent }} />
          )}
        </article>
      </section>

      <section className="dashboard-overview-grid dashboard-overview-grid--secondary">
        <article className="dashboard-card">
          <p className="dashboard-callout-kicker">Kommande bokning</p>
          {nextBooking ? (
            <>
              <h3>{nextBooking.date}</h3>
              <p>{nextBooking.startTime} - {nextBooking.endTime}</p>
              <span className="dashboard-category">{nextBooking.status}</span>
            </>
          ) : (
            <p>Inga bokningar just nu.</p>
          )}
        </article>

        <article className="dashboard-card">
          <p className="dashboard-callout-kicker">Kommande seminarium</p>
          {nextSeminar ? (
            <>
              <h3>{nextSeminar.title}</h3>
              <p>{nextSeminar.date} {nextSeminar.time ? `• ${nextSeminar.time}` : ""}</p>
              <p>{nextSeminar.description}</p>
            </>
          ) : (
            <p>Inga seminarier just nu. Nya tider läggs upp löpande.</p>
          )}
        </article>
      </section>

      {activeRebookRequests.length > 0 || expiredRebookRequests.length > 0 ? (
        <section className="dashboard-card dashboard-card--highlight">
          <p className="dashboard-callout-kicker">Ombokning</p>
          <h3>{activeRebookRequests.length > 0 ? "Du har erbjudna tider" : "Inga aktiva ombokningar"}</h3>
          <p>
            {activeRebookRequests.length > 0
              ? "Välj en av tiderna nedan för att bekräfta din nya bokning."
              : "Eventuella tidigare förfrågningar har gått ut."}
          </p>

          <div style={{ display: "grid", gap: 12 }}>
            {activeRebookRequests.map((request) => (
              <div key={request.id} style={{ border: "1px solid hsl(var(--border))", borderRadius: 16, padding: 16 }}>
                <p style={{ margin: 0, fontWeight: 600 }}>
                  Förfrågan #{request.id}
                </p>
                <p style={{ marginTop: 4, marginBottom: 12, color: "hsl(var(--muted-foreground))" }}>
                  Går ut {format(new Date(request.expiresAt), "d MMM HH:mm", { locale: sv })}
                </p>
                  <span className="dashboard-category" style={{ display: "inline-block", marginBottom: 12 }}>
                    {getRequestStatusText(request.status)}
                  </span>
                  <span className="dashboard-category" style={{ display: "inline-block", marginBottom: 12, marginLeft: 8 }}>
                    {getCategoryText(request.bookingCategory)}
                  </span>

                <div style={{ display: "grid", gap: 8 }}>
                  {request.proposedSlots.map((slot) => (
                    <button
                      key={slot.id}
                      type="button"
                      className="dashboard-course-cta"
                      onClick={() => navigate(buildRebookAcceptUrl(request.id, slot, request.bookingCategory))}
                    >
                      {format(new Date(slot.date), "d MMM", { locale: sv })} {slot.startTime} - {slot.endTime}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="dashboard-course-cta"
                    style={{ background: "transparent", color: "hsl(var(--foreground))", border: "1px solid hsl(var(--border))" }}
                    onClick={async () => {
                      const ok = await DataService.BookingService.DeclineRebookProposal(request.id);
                      if (ok) {
                        void loadData();
                      }
                    }}
                  >
                    Neka förslaget
                  </button>

                  <button
                    type="button"
                    className="dashboard-course-cta"
                    style={{ background: "transparent", color: "hsl(var(--foreground))", border: "1px solid hsl(var(--border))" }}
                    onClick={() => {
                      setCounterRequestId(request.id);
                      setCounterSlots([]);
                      setCounterDate("");
                      setCounterTime("");
                    }}
                  >
                    Föreslå alternativa tider
                  </button>

                  {counterRequestId === request.id && (
                    <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
                      <div style={{ display: "grid", gap: 8 }}>
                        <input
                          type="date"
                          value={counterDate}
                          onChange={(e) => setCounterDate(e.target.value)}
                          className="card-managedates-add-time-input"
                        />
                        <input
                          type="time"
                          value={counterTime}
                          onChange={(e) => setCounterTime(e.target.value)}
                          className="card-managedates-add-time-input"
                        />
                        <button type="button" className="dashboard-course-cta" onClick={addCounterSlot}>
                          Lägg till tid
                        </button>
                      </div>

                      {counterSlots.length > 0 && (
                        <div style={{ display: "grid", gap: 8 }}>
                          {counterSlots.map((slot, index) => (
                            <div key={`${slot.date}-${slot.startTime}-${index}`} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                              <span>{format(new Date(slot.date), "d MMM", { locale: sv })} {slot.startTime}</span>
                              <button
                                type="button"
                                onClick={() => setCounterSlots((prev) => prev.filter((_, i) => i !== index))}
                                className="dashboard-course-cta"
                                style={{ padding: "0.4rem 0.8rem" }}
                              >
                                Ta bort
                              </button>
                            </div>
                          ))}
                          <button type="button" className="dashboard-course-cta" onClick={() => submitCounterOffer(request.id)}>
                            Skicka motförslag
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {expiredRebookRequests.length > 0 && (
              <div style={{ border: "1px dashed hsl(var(--border))", borderRadius: 16, padding: 16, opacity: 0.85 }}>
                <p style={{ margin: 0, fontWeight: 600 }}>Utgångna förfrågningar</p>
                <p style={{ marginTop: 8, marginBottom: 0, color: "hsl(var(--muted-foreground))" }}>
                  {expiredRebookRequests.length} ombokning{expiredRebookRequests.length > 1 ? "ar" : ""} gick ut innan du hann svara.
                </p>
              </div>
            )}
          </div>
        </section>
      ) : null}

      {history?.orders?.[0] && (
        <section className="dashboard-card dashboard-card--subtle">
          <p className="dashboard-callout-kicker">Senaste order</p>
          <h3>{history.orders[0].orderNumber}</h3>
          <p>{history.orders[0].items.map((item) => item.title).join(", ")}</p>
        </section>
      )}
    </div>
  );
};
